import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Select, SimpleGrid, TextInput } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { Stack } from '@mui/material'
import { IconHash, IconRefresh, IconZoom } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useGetList, useNotify, useTranslate } from 'react-admin'
import {
  getApiVideoPromotionChannelsYoutubeChannelIdSyncVideosJobId,
  getGetApiVideoPromotionVideosQueryKey,
  useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists,
  usePostApiVideoPromotionChannelsYoutubeChannelIdSyncVideos,
} from '../../../codegen/generated/video-promotion'
import { useVideoPromotionStore } from '../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../types/YoutubeChannel'
import { SyncChannelVideosModal } from './SyncChannelVideosModal'

const SYNC_STATUS_POLL_INTERVAL_MS = 2000

const waitForChannelVideosSync = async (youtubeChannelId: string, jobId: string) => {
  for (;;) {
    const { body } = await getApiVideoPromotionChannelsYoutubeChannelIdSyncVideosJobId(
      youtubeChannelId,
      jobId,
    )
    if (body.status !== 'pending') {
      return body
    }
    await new Promise(resolve => setTimeout(resolve, SYNC_STATUS_POLL_INTERVAL_MS))
  }
}

export const NewPromotionVideosSearch = () => {
  const t = useTranslate()
  const notify = useNotify()
  const videoPromotionStore = useVideoPromotionStore()
  const queryClient = useQueryClient()
  const [isSyncing, setIsSyncing] = useState(false)

  const filters = videoPromotionStore.selectionVideoStaticFilters
  const [search, setSearch] = useState(filters?.search ?? '')
  const [videoId, setVideoId] = useState(filters?.videoId ?? '')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [debouncedVideoId] = useDebouncedValue(videoId, 300)

  useEffect(() => {
    videoPromotionStore.setSelectionVideoStaticFilters({
      search: debouncedSearch || undefined,
      videoId: debouncedVideoId || undefined,
    })
  }, [debouncedSearch, debouncedVideoId, videoPromotionStore.setSelectionVideoStaticFilters])

  const syncChannelVideosMutation = usePostApiVideoPromotionChannelsYoutubeChannelIdSyncVideos()
  const [isSyncModalOpened, syncModal] = useDisclosure(false)
  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const youtubeChannels = useMemo(
    () =>
      (youtubeChannelsQuery.data ?? []).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [youtubeChannelsQuery.data],
  )

  const playlistsQuery = useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists(
    videoPromotionStore.youtubeChannelId ?? '',
    {
      query: {
        enabled: !!videoPromotionStore.youtubeChannelId,
      },
    },
  )
  const playlists = useMemo(
    () =>
      (playlistsQuery.data?.body.playlists ?? []).map(b => ({
        value: b.id,
        label: b.title,
      })),
    [playlistsQuery.data],
  )

  const syncVideos = useCallback(
    async ({ includeUnlisted, fullResync }: { includeUnlisted: boolean; fullResync: boolean }) => {
      const youtubeChannelId = videoPromotionStore.youtubeChannelId
      if (!youtubeChannelId) {
        return
      }
      setIsSyncing(true)
      try {
        const { body } = await syncChannelVideosMutation.mutateAsync({
          youtubeChannelId,
          data: { includeUnlisted, fullResync },
        })
        if (!body.jobId) {
          throw new Error('Missing sync job id')
        }
        const status = await waitForChannelVideosSync(youtubeChannelId, body.jobId)
        if (status.status === 'failed') {
          notify('pages.videoPromotion.errors.syncChannelVideos', {
            type: 'error',
            messageArgs: { reason: status.failedReason ?? '' },
          })
          return
        }
        const smart_count = status.result?.videosCount ?? 0
        if (status.result?.uploadsPlaylistMissing && !includeUnlisted) {
          notify(
            smart_count === 0
              ? 'pages.videoPromotion.success.syncChannelVideos.noVideoWithoutUploadsPlaylist'
              : 'pages.videoPromotion.success.syncChannelVideos.withoutUploadsPlaylist',
            {
              type: 'warning',
              messageArgs: { smart_count },
              autoHideDuration: 15000,
            },
          )
        } else {
          notify('pages.videoPromotion.success.syncChannelVideos.description', {
            type: 'success',
            messageArgs: { smart_count },
          })
        }
        syncModal.close()
        useVideoPromotionStore.setState({ videosSyncedAt: Date.now() })
        queryClient.invalidateQueries({
          queryKey: [getGetApiVideoPromotionVideosQueryKey({ youtubeChannelId })[0]],
        })
        playlistsQuery.refetch()
      } catch (e: unknown) {
        const message =
          (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e
        notify(message, { type: 'error' })
      } finally {
        setIsSyncing(false)
      }
    },
    [
      syncChannelVideosMutation.mutateAsync,
      notify,
      videoPromotionStore.youtubeChannelId,
      playlistsQuery.refetch,
      syncModal.close,
      queryClient,
    ],
  )

  return (
    <SimpleGrid cols={4} mt="xl" mb="sm">
      <Stack alignItems="flex-start" gap={1}>
        <Select
          label={t('_generics.youtubeChannel')}
          data={youtubeChannels}
          value={videoPromotionStore.youtubeChannelId}
          onChange={ytCID => videoPromotionStore.setYoutubeChannelId(ytCID ?? undefined)}
          searchable
          clearable
          autoComplete="off"
          data-form-type="other"
          w="100%"
        />
        {!!videoPromotionStore.youtubeChannelId && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={syncModal.open}
            leftSection={<IconRefresh size={12} />}
          >
            {t('pages.videoPromotion.syncChannelVideos')}
          </Button>
        )}
      </Stack>
      <Select
        label={t('_generics.playlist')}
        data={playlists}
        nothingFoundMessage={t('pages.videoPromotion.noPlaylist')}
        value={filters?.playlistId ?? null}
        onChange={playlistId =>
          videoPromotionStore.setSelectionVideoStaticFilters({
            playlistId: playlistId ?? undefined,
          })
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
        disabled={
          typeof videoPromotionStore.youtubeChannelId === 'undefined' || playlistsQuery.isLoading
        }
      />
      <TextInput
        leftSection={<IconZoom size={12} />}
        label={t('pages.videoPromotion.videoName')}
        value={search}
        onChange={event => setSearch(event.currentTarget.value)}
        autoComplete="off"
        data-form-type="other"
      />
      <TextInput
        leftSection={<IconHash size={12} />}
        label={t('pages.videoPromotion.videoId')}
        value={videoId}
        onChange={event => setVideoId(event.currentTarget.value)}
        autoComplete="off"
        data-form-type="other"
      />
      <SyncChannelVideosModal
        opened={isSyncModalOpened}
        loading={isSyncing}
        onClose={syncModal.close}
        onSync={syncVideos}
      />
    </SimpleGrid>
  )
}
